/**
 * What a partner pastes: the embed, the CSP their page must allow
 * (contracts/widget-config.md §CSP, plus spec 013 plan.md §5 when live mode is
 * requested), and the tier-3 ownership file. Pure functions so the Install
 * screen, the email-to-developer body and the tests all read one source.
 */
import { API_BASE, WIDGET_SCRIPT_URL } from '../env';

export const LOADER_URL = WIDGET_SCRIPT_URL;
const CDN_ORIGIN = (() => {
  try {
    return new URL(WIDGET_SCRIPT_URL).origin;
  } catch {
    return 'https://cdn.finderlyfix.com';
  }
})();

/** The API the widget calls when the element has no `api-base`. */
const WIDGET_DEFAULT_API = 'https://s.finderlyfix.com';

/**
 * ` api-base="…"` when this portal talks to another API than the widget's
 * compiled default, else nothing. Every widget build defaults to production,
 * so without it a snippet copied from the internal portal sent an internal key
 * to production and got 401 (2026-09-26 summit.25x.codes install).
 */
export function apiBaseAttr(apiBase: string = API_BASE): string {
  return apiBase.replace(/\/+$/, '') === WIDGET_DEFAULT_API ? '' : ` api-base="${apiBase}"`;
}

export function embedSnippet(siteKey: string, options: { test?: boolean; apiBase?: string } = {}): string {
  return [widgetElement(siteKey, options), loaderTag()].join('\n');
}

/**
 * The one test-key snippet: the Install page's "Test key" and the Test a lead
 * step both show this. `mode="test"` only badges the widget "test" for the
 * person trying it; the lead's real mode comes from the key (FR-014).
 */
export function testSnippet(testKey: string, options: { apiBase?: string } = {}): string {
  return embedSnippet(testKey, { ...options, test: true });
}

/** The element alone: where the widget appears. */
export function widgetElement(siteKey: string, options: { test?: boolean; apiBase?: string } = {}): string {
  const mode = options.test ? ' mode="test"' : '';
  return `<finderly-diagnosis-widget site-key="${siteKey}"${mode}${apiBaseAttr(options.apiBase)}></finderly-diagnosis-widget>`;
}

/** The loader alone: loads the widget, once per page. */
export function loaderTag(): string {
  return `<script src="${LOADER_URL}" async></script>`;
}

/** React / Next.js (a): the loader, once, in the root layout. */
export function nextLayoutSnippet(): string {
  return `import Script from 'next/script';

export default function RootLayout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <body>
        {children}
        <Script src="${LOADER_URL}" strategy="afterInteractive" />
      </body>
    </html>
  );
}`;
}

/**
 * React / Next.js (b): the component, with the JSX type for the element. The
 * type lists only what a partner sets (`site-key`, `mode`); `api-base` is
 * an internal-stack attribute and appears only when this portal is not the
 * production one (see apiBaseAttr).
 */
export function reactSnippet(siteKey: string, options: { test?: boolean; apiBase?: string } = {}): string {
  const extra = apiBaseAttr(options.apiBase);
  const mode = options.test ? ' mode="test"' : '';
  return `import type { DetailedHTMLProps, HTMLAttributes } from 'react';

declare module 'react' {
  namespace JSX {
    interface IntrinsicElements {
      'finderly-diagnosis-widget': DetailedHTMLProps<HTMLAttributes<HTMLElement>, HTMLElement> & {
        'site-key': string;
        mode?: 'live' | 'test';${extra ? "\n        'api-base'?: string;" : ''}
      };
    }
  }
}

export function RepairHelp() {
  return <finderly-diagnosis-widget site-key="${siteKey}"${mode}${extra} />;
}`;
}

/** Which LiveKit-backed modes the site's published config turns on. */
export interface CspModes {
  live?: boolean;
  chat?: boolean;
}

/**
 * The directives the partner's page needs. `live` adds spec 013's LiveKit
 * signalling hosts and the media-src for the agent's audio element; `chat`
 * (spec 014) the same hosts without media.
 */
export function cspDirectives(apiBase: string, options: CspModes = {}): Array<[string, string]> {
  const api = new URL(apiBase);
  const wss = `wss://${api.host}`;
  const connect = [api.origin, 'https://app.finderlyfix.com'];
  if (options.live || options.chat) {
    for (const host of [wss, 'wss://livekit.finderlyfix.com']) {
      // After a failed connect livekit-client sends an HTTPS validation
      // request to the same host; blocked, the visitor gets a CSP error in
      // place of the real reason. The API's own origin is already listed.
      const https = host.replace(/^wss:/, 'https:');
      connect.push(host);
      if (!connect.includes(https)) connect.push(https);
    }
  }
  const rows: Array<[string, string]> = [
    ['script-src', CDN_ORIGIN],
    ['connect-src', connect.join(' ')],
    ['img-src', `${CDN_ORIGIN} blob:`],
    // The contact step's frame (decisions/trustedform-widget.md option C),
    // served next to the bundle. Only a site with the eLocal box uses it, but
    // it is listed for every site: the box can be switched on later, and a
    // missing source fails quietly (the widget falls back to its own form and
    // the lead goes without a TrustedForm certificate).
    ['frame-src', CDN_ORIGIN],
    // No style-src row (#876): the widget styles its shadow root with an
    // adopted constructable stylesheet, which style-src does not govern.
    // Only engines without one (Safari before 16.4) fall back to a <style>
    // element that needs 'unsafe-inline'; cspCaveats says so.
    // See docs/security/widget-csp.md.
  ];
  if (options.live) rows.push(['media-src', 'blob: mediastream:']);
  return rows;
}

/** The directives as the API's CSP check takes them (`POST …/csp-check`). */
export function cspRequirements(apiBase: string, options: CspModes = {}): Array<{ directive: string; sources: string[] }> {
  return cspDirectives(apiBase, options).map(([directive, value]) => ({ directive, sources: value.split(' ').filter(Boolean) }));
}

/**
 * Paste-ready directives, one per line: `script-src https://cdn…;`. No
 * comments (they are not valid in a header value); the caveats are
 * `cspCaveats`, shown as help text beside the block.
 */
export function cspLines(rows: Array<[string, string]>): string {
  return rows.map(([d, v]) => `${d} ${v};`).join('\n');
}

export function cspSnippet(apiBase: string, options: CspModes = {}): string {
  return cspLines(cspDirectives(apiBase, options));
}

/**
 * Only the sources a site's policy is missing (from the API's CSP check),
 * grouped by the directive to edit — `default-src` when the policy has no
 * directive of its own for them.
 */
export function missingCspSnippet(missing: Array<{ add_to: string; sources: string[] }>): string {
  const byDirective = new Map<string, string[]>();
  for (const m of missing) {
    const list = byDirective.get(m.add_to) ?? [];
    for (const s of m.sources) if (!list.includes(s)) list.push(s);
    byDirective.set(m.add_to, list);
  }
  return cspLines([...byDirective].map(([d, sources]) => [d, sources.join(' ')]));
}

/** The caveats that used to be `#` comments in the snippet (#876, spec 013). */
export function cspCaveats(options: { live?: boolean } = {}): string[] {
  const notes = [
    "If your script-src uses nonces or hashes, add 'strict-dynamic' and put your nonce on the loader <script> tag: the loader injects the widget bundle.",
    "No style-src source is needed. Only Safari before 16.4 needs style-src 'unsafe-inline' to style the widget (not with a nonce or hash).",
  ];
  if (options.live) {
    notes.push(
      'Live conversation: WebRTC media (UDP 7882 / TCP 7881) is not governed by CSP, but a firewall must allow it, and Permissions-Policy must not deny microphone.',
    );
  }
  return notes;
}

/** Tier 3's file, served from the root of the website. */
export const WELL_KNOWN_FILE = '/.well-known/finderly-site-verification.txt';

/** Tier 3: `https://<origin>/.well-known/finderly-site-verification.txt` must contain the key. */
export function wellKnownPath(origin: string): string {
  return `${origin.replace(/\/+$/, '')}${WELL_KNOWN_FILE}`;
}

export interface SnippetBlock {
  caption: string;
  code: string;
}

export interface Platform {
  id: string;
  name: string;
  /** What to do, in order. One step renders as a sentence, more as a numbered list. */
  steps: string[];
  /** The code to paste; the default is the two-line embed. */
  blocks?: (siteKey: string, options: { test?: boolean }) => SnippetBlock[];
}

const keyNote = (test?: boolean) => (test ? 'test key: leads are marked test and never routed' : 'live key');

export const PLATFORMS: Platform[] = [
  { id: 'html', name: 'HTML', steps: ['Paste the snippet inside <body>, where the widget should appear.'] },
  {
    id: 'wordpress',
    name: 'WordPress',
    steps: ['Edit the page and add a “Custom HTML” block where the widget should go.', 'Paste the snippet and update the page.'],
  },
  { id: 'webflow', name: 'Webflow', steps: ['Drag an “Embed” element onto the page.', 'Paste the snippet, save, and publish the site.'] },
  { id: 'squarespace', name: 'Squarespace', steps: ['Add a “Code” block to the page.', 'Paste the snippet and turn off “Display source”.'] },
  { id: 'wix', name: 'Wix', steps: ['Choose Add → Embed Code → Embed HTML.', 'Choose “Code”, paste the snippet, and make the frame at least 640 px tall.'] },
  { id: 'shopify', name: 'Shopify', steps: ['Go to Online Store → Themes → Customize.', 'Add a “Custom Liquid” section and paste the snippet.'] },
  {
    id: 'gtm',
    name: 'Google Tag Manager',
    steps: [
      'Put the element in the page itself, where the widget should appear. Tag Manager cannot place it.',
      'In Tag Manager, create a Custom HTML tag with only the script line, and fire it on the pages that carry the element.',
    ],
    blocks: (key, { test }) => [
      { caption: `On the page · ${keyNote(test)}`, code: widgetElement(key, { test }) },
      { caption: 'In GTM · Custom HTML tag', code: loaderTag() },
    ],
  },
  {
    id: 'react',
    name: 'React / Next.js',
    steps: [
      'Load the widget script once, in your root layout. Plain React: add the script tag to index.html instead.',
      'Render the element where the widget should appear.',
    ],
    blocks: (key, { test }) => [
      { caption: 'Root layout · app/layout.tsx', code: nextLayoutSnippet() },
      { caption: `Component · ${keyNote(test)}`, code: reactSnippet(key, { test }) },
    ],
  },
  {
    id: 'hubspot',
    name: 'HubSpot',
    steps: ['In the page editor, add a “Custom HTML” module (on older themes: Rich text → Source code).', 'Paste the snippet and publish the page.'],
  },
  { id: 'appfolio', name: 'AppFolio', steps: ['Go to Website → Pages and edit the page.', 'Add an HTML or embed block and paste the snippet.'] },
  { id: 'buildium', name: 'Buildium', steps: ['Go to Website → Pages and edit the page.', 'Switch the content area to HTML and paste the snippet.'] },
  {
    id: 'entrata',
    name: 'Entrata',
    steps: ['Ask your Entrata website contact to add the snippet to the page.', 'Send them everything they need with “Email these instructions” below.'],
  },
  { id: 'realpage', name: 'RealPage', steps: ['In the site builder, add a custom HTML widget.', 'Paste the snippet and publish.'] },
];

/** The code blocks a platform shows for this key. */
export function platformBlocks(platform: Platform, siteKey: string, options: { test?: boolean } = {}): SnippetBlock[] {
  if (platform.blocks) return platform.blocks(siteKey, options);
  return [{ caption: `Snippet · ${keyNote(options.test)}`, code: embedSnippet(siteKey, options) }];
}

export function developerEmail(args: {
  orgName: string;
  siteKey: string;
  origins: string[];
  /** Migration 059: 'any' (default) needs no domain setup at all. */
  originPolicy?: 'any' | 'listed';
  apiBase: string;
  live: boolean;
  /** Spec 014: chat needs the LiveKit hosts too. */
  chat?: boolean;
  portalUrl: string;
}): { subject: string; body: string } {
  const subject = `Install the FinderlyFix widget for ${args.orgName}`;
  const body = [
    `Hi,`,
    ``,
    `Please add the FinderlyFix diagnosis widget to our site.`,
    ``,
    `1. Paste this where the widget should appear:`,
    ``,
    embedSnippet(args.siteKey),
    ``,
    args.originPolicy === 'listed'
      ? `2. It only works on these websites: ${args.origins.join(', ') || '(none listed yet)'}.`
      : `2. It works on any https website; there is no domain to set up.`,
    ``,
    `3. Only if the site sends a Content-Security-Policy: add these sources to it.`,
    ``,
    cspSnippet(args.apiBase, { live: args.live, chat: args.chat }),
    ``,
    ...cspCaveats({ live: args.live }).map((note) => `   - ${note}`),
    ``,
    `4. Optional ownership check (the only way to verify the website is ours; a page scan only checks the install): serve ${args.origins[0] ? wellKnownPath(args.origins[0]) : '/.well-known/finderly-site-verification.txt'} containing the key ${args.siteKey}.`,
    ``,
    `Status: ${args.portalUrl}/setup/verify`,
  ].join('\n');
  return { subject, body };
}
